import { createFileRoute, redirect } from '@tanstack/react-router';
import { z } from 'zod';
import { isForbiddenError, isNotFoundError } from '@/api/errors';
import { clusterService } from '@/api/services/cluster';
import { clusterDataService } from '@/api/services/cluster/cluster-data';
import { monitoringService } from '@/api/services/monitoring';
import { safeAsync } from '@/helpers/errors/safeAsync';
import { validateParams } from '@/router/utils';
import { clusterIdUrlParam } from './route.hooks';

export const Route = createFileRoute('/_authenticated/_dashboard/accounts/$accountId/clusters/$clusterId')({
  params: validateParams(
    z.object({
      clusterId: clusterIdUrlParam,
    }),
  ),
  beforeLoad: async ({ context: { queryClient }, matches, params }) => {
    const { accountId } = params;
    const lastMatch = matches.at(-1);

    const clusterQuery = clusterService.getClusterQuery(params);
    // Fetching the cluster is also the gate: the permissions interceptor throws an AccessControlError
    // when the user lacks `read:clusters`. That one is deliberately not redirected — it falls through to
    // the rethrow below, where its `isNotFound` renders the "Access Denied" screen.
    const [clusterError] = await safeAsync(queryClient.fetchQuery(clusterQuery));
    if (clusterError) {
      if (isNotFoundError(clusterError) || isForbiddenError(clusterError)) {
        throw redirect({
          to: '/accounts/$accountId/clusters',
          params: { accountId },
          replace: true,
        });
      }
      throw clusterError;
    }
    if (lastMatch?.routeId === Route.id) {
      throw redirect({
        to: '/accounts/$accountId/clusters/$clusterId/overview',
        params,
        replace: true,
      });
    }

    return {
      clusterQuery,
      clusterCollectionQueryOptions: clusterDataService.listCollectionsQuery(params),
      clustersByAccountIdQuery: clusterService.listClustersQuery({ accountId }),
      clusterSummaryMetricsQuery: monitoringService.getClusterSummaryMetricsQuery(params),
      clusterAlertsQuery: monitoringService.listClusterAlertsQuery(params),
    };
  },
  loader: ({ context: { queryClient, clusterAlertsQuery, clustersByAccountIdQuery } }) => {
    void queryClient.prefetchQuery(clustersByAccountIdQuery);
    void queryClient.prefetchQuery(clusterAlertsQuery);
  },
});
